import { useThemeMode } from '@/hooks/useThemeMode';
import { getSmartEdge, GetSmartEdgeOptions } from '@tisoap/react-flow-smart-edge';
import { BaseEdge, EdgeProps, Node, StepEdge, useNodes } from '@xyflow/react';
import { FC, memo, useRef } from 'react';
import { pathfindingAStarNoDiagonal, svgDrawStraightLinePath } from './pathfindingAStarNoDiagonal';

const options: GetSmartEdgeOptions = {
  drawEdge: svgDrawStraightLinePath,
  generatePath: pathfindingAStarNoDiagonal
};

type CachedPath = {
  path: string;
  centerX: number;
  centerY: number;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  nodesMeasured: boolean;
  layoutKey: number;
  gridRatio: number;
  nodePadding: number;
};

let cachedLayoutKey = 0;
let cachedNodesRef: Node[] | null = null;
let cachedGridRatio = 0;

const getLayoutKey = (nodes: Node[], gridRatio: number): number => {
  if (nodes === cachedNodesRef && gridRatio === cachedGridRatio) return cachedLayoutKey;

  let key = nodes.length;
  for (const node of nodes) {
    key += Math.round(node.position.x / gridRatio) * 73 + Math.round(node.position.y / gridRatio) * 37;
  }

  cachedNodesRef = nodes;
  cachedLayoutKey = key;
  cachedGridRatio = gridRatio;
  return key;
};

let cachedMeasured = false;
let cachedMeasuredNodesRef: Node[] | null = null;

const areNodesMeasured = (nodes: Node[]): boolean => {
  if (nodes === cachedMeasuredNodesRef) return cachedMeasured;
  cachedMeasuredNodesRef = nodes;
  cachedMeasured = nodes.every((node) => node.measured?.width && node.measured?.height);
  return cachedMeasured;
};

const SmartEdgeCached: FC<EdgeProps> = (props) => {
  const { id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style, markerEnd, markerStart } =
    props;

  const nodes = useNodes();
  const { gridRatio, nodePadding } = useThemeMode();
  const gridRatioNum = Number(gridRatio);
  const nodePaddingNum = Number(nodePadding);
  const cacheRef = useRef<CachedPath>(null);
  const measured = areNodesMeasured(nodes);
  const layoutKey = getLayoutKey(nodes, gridRatioNum);

  if (
    cacheRef.current &&
    cacheRef.current.nodesMeasured &&
    cacheRef.current.gridRatio === gridRatioNum &&
    cacheRef.current.nodePadding === nodePaddingNum &&
    cacheRef.current.layoutKey === layoutKey &&
    cacheRef.current.sourceX === sourceX &&
    cacheRef.current.sourceY === sourceY &&
    cacheRef.current.targetX === targetX &&
    cacheRef.current.targetY === targetY
  ) {
    return (
      <BaseEdge id={id} path={cacheRef.current.path} style={style} markerEnd={markerEnd} markerStart={markerStart} />
    );
  }

  if (!measured) {
    return <StepEdge {...props} />;
  }

  const result = getSmartEdge({
    sourcePosition,
    targetPosition,
    sourceX,
    sourceY,
    targetX,
    targetY,
    nodes,
    options: { ...options, gridRatio: gridRatioNum, nodePadding: nodePaddingNum }
  });

  if (!result || result instanceof Error) {
    if (cacheRef.current) {
      return (
        <BaseEdge id={id} path={cacheRef.current.path} style={style} markerEnd={markerEnd} markerStart={markerStart} />
      );
    }
    return null;
  }

  cacheRef.current = {
    path: result.svgPathString,
    centerX: result.edgeCenterX,
    centerY: result.edgeCenterY,
    sourceX,
    sourceY,
    targetX,
    targetY,
    nodesMeasured: true,
    layoutKey,
    gridRatio: gridRatioNum,
    nodePadding: nodePaddingNum
  };

  return <BaseEdge id={id} path={result.svgPathString} style={style} markerEnd={markerEnd} markerStart={markerStart} />;
};

const MemoizedSmartEdgeCached = memo(SmartEdgeCached, (prev, next) => {
  return (
    prev.source === next.source &&
    prev.target === next.target &&
    prev.sourceX === next.sourceX &&
    prev.sourceY === next.sourceY &&
    prev.targetX === next.targetX &&
    prev.targetY === next.targetY &&
    prev.sourcePosition === next.sourcePosition &&
    prev.targetPosition === next.targetPosition &&
    prev.selected === next.selected &&
    prev.sourceHandleId === next.sourceHandleId &&
    prev.targetHandleId === next.targetHandleId
  );
});

export default MemoizedSmartEdgeCached;
